// 130 Spectator Filter -- pure, unit-testable filtering for the events
// timetable. The chip scope resolves to a set of school IDs; sport + search
// narrow further. Operates on the minimal shape both EventPublic and
// CalendarEvent satisfy, so the same logic serves the personal feed
// (/me/calendar) and the global list (/events).

export type FilterScope = "my_kids" | "my_schools" | "my_favourites" | "all";

export interface EventFilterState {
  scope: FilterScope;
  /** Sport names; multi-select. Empty = all sports. */
  sports: string[];
  /** Free-text search over event + school/opposition names. */
  q: string;
}

export interface SchoolSets {
  /** Schools the user's linked children attend. */
  kidSchoolIds: Set<string>;
  /** Schools the user follows (UserPreference.followed_school_ids). */
  favSchoolIds: Set<string>;
  /** Schools the user has a role at (UserRole, scope_type=school). */
  roleSchoolIds: Set<string>;
}

/** Minimal event fields the filter reads. EventPublic / CalendarEvent satisfy this. */
export interface FilterableEvent {
  school_id: string;
  school_name: string;
  name: string;
  sport_name: string | null;
  schools: { school_id: string; name: string }[];
}

/** True if the event's host or any participating school is in the set. */
function eventInSchoolSet(event: FilterableEvent, ids: Set<string>): boolean {
  if (ids.size === 0) return false;
  if (ids.has(event.school_id)) return true;
  return event.schools.some((s) => ids.has(s.school_id));
}

export function eventMatchesScope(
  event: FilterableEvent,
  scope: FilterScope,
  sets: SchoolSets,
): boolean {
  switch (scope) {
    case "all":
      return true;
    case "my_kids":
      return eventInSchoolSet(event, sets.kidSchoolIds);
    case "my_favourites":
      return eventInSchoolSet(event, sets.favSchoolIds);
    case "my_schools":
      // A parent's "schools" are their children's schools plus any role schools.
      return eventInSchoolSet(
        event,
        new Set([...sets.roleSchoolIds, ...sets.kidSchoolIds]),
      );
  }
}

export function eventMatchesSports(event: FilterableEvent, sports: string[]): boolean {
  if (sports.length === 0) return true;
  if (!event.sport_name) return false;
  const name = event.sport_name.toLowerCase();
  return sports.some((s) => s.toLowerCase() === name);
}

export function eventMatchesSearch(event: FilterableEvent, q: string): boolean {
  const term = q.trim().toLowerCase();
  if (!term) return true;
  const haystacks = [
    event.name,
    event.school_name,
    ...event.schools.map((s) => s.name),
  ];
  return haystacks.some((h) => h?.toLowerCase().includes(term));
}

export function filterEvents<E extends FilterableEvent>(
  events: E[],
  state: EventFilterState,
  sets: SchoolSets,
): E[] {
  return events.filter(
    (e) =>
      eventMatchesScope(e, state.scope, sets) &&
      eventMatchesSports(e, state.sports) &&
      eventMatchesSearch(e, state.q),
  );
}

/** Role default: land on the most personal chip the user has data for. */
export function resolveDefaultScope(sets: SchoolSets): FilterScope {
  if (sets.kidSchoolIds.size > 0) return "my_kids";
  if (sets.favSchoolIds.size > 0) return "my_favourites";
  if (sets.roleSchoolIds.size > 0) return "my_schools";
  return "all";
}

/**
 * Which chips to show. My Kids needs linked children; My Schools needs a role
 * or children; My Favourites + All are always offered (Favourites teaches the
 * star affordance even before anything is followed).
 */
export function visibleScopes(sets: SchoolSets): FilterScope[] {
  const scopes: FilterScope[] = [];
  if (sets.kidSchoolIds.size > 0) scopes.push("my_kids");
  if (sets.roleSchoolIds.size > 0 || sets.kidSchoolIds.size > 0) {
    scopes.push("my_schools");
  }
  scopes.push("my_favourites", "all");
  return scopes;
}

export const SCOPE_LABELS: Record<FilterScope, string> = {
  my_kids: "My Kids",
  my_schools: "My Schools",
  my_favourites: "My Favourites",
  all: "All",
};

/**
 * Which scopes draw from the /me/calendar feed vs the global /events list.
 * Only "My Kids" needs the participation-filtered calendar; My Schools /
 * My Favourites / All are pure school-set filters over the global list, so
 * they reflect favourite changes instantly (no refetch) and don't depend on
 * what was followed at calendar-fetch time.
 */
export function usesCalendarFeed(scope: FilterScope): boolean {
  return scope === "my_kids";
}

export function parseScope(raw: string | null): FilterScope | null {
  if (raw === "my_kids" || raw === "my_schools" || raw === "my_favourites" || raw === "all") {
    return raw;
  }
  return null;
}
